/* The kit's entries of the component gallery (Fase D, 2026-10-24).
 *
 * Ported from the review bench (`omnitok-ui-prototype/src/kit/ui-catalog.jsx`):
 * one live demo per @omnitok/ui v3 component, plus the dashboard-render
 * pieces that read as page chrome (report tabs, section header, message
 * cards, the collapsible filter header). Plain JSX, typed by
 * `kit-demos.d.ts`, like bi-components.
 *
 * Sample data is NEUTRAL on purpose (D13): the builder is multi-tenant, and
 * a client's admin must never see another client's name in the gallery.
 */
import { useState } from "react";
import * as UI from "@omnitok/ui";
import {
  CollapsibleFilterHeader,
  DashboardTabs,
  MessageCard,
  SectionHeader,
} from "@pervasivemind/dashboard-render";
import {
  ArrowUpRight,
  BarChart3,
  Check,
  Database,
  Download,
  Folder,
  Home,
  LayoutDashboard,
  ListChecks,
  Plus,
  Search,
  Settings,
} from "lucide-react";

export const ROW = "flex flex-wrap items-center gap-2";
export const STACK = "flex flex-col gap-3.5";
export const FIELDS = "grid gap-[18px]";
export const DESC = "text-2xs leading-[1.7] text-neutral-500";

const opts = [
  { value: "a", label: "Tienda A" },
  { value: "b", label: "Tienda B" },
  { value: "c", label: "Tienda C" },
  { value: "blocked", label: "No disponible", disabled: true },
];
const items = [
  { id: "summary", label: "Resumen", icon: <LayoutDashboard size={14} /> },
  { id: "retailers", label: "Retailers", icon: <Folder size={14} /> },
  { id: "products", label: "Productos", badge: 24 },
  { id: "trend", label: "Evolución" },
];

const Btn = ({ children, kind = "secondary", ...props }) => (
  <UI.Button variant={kind} {...props}>
    {children}
  </UI.Button>
);

function Buttons() {
  const toast = UI.useToast();
  return (
    <div className={STACK}>
      <div className={ROW}>
        <Btn kind="primary" leftIcon={<Plus size={15} />} onClick={() => toast.success("Acción de ejemplo ejecutada")}>Crear reporte</Btn>
        <Btn leftIcon={<Download size={15} />} onClick={() => toast.info("Exportación de ejemplo")}>Exportar</Btn>
        <UI.Button variant="outline" onClick={() => toast.info("Acción secundaria")}>Cancelar</UI.Button>
        <UI.Button ghost>Ver detalle <ArrowUpRight size={13} /></UI.Button>
      </div>
      <div className={ROW}>
        <Btn kind="primary" loading>Guardando</Btn>
        <Btn disabled>Deshabilitado</Btn>
        {["xs", "sm", "md", "lg"].map((size) => <Btn size={size} key={size}>{size.toUpperCase()}</Btn>)}
      </div>
      <div className={ROW}>
        {["accept", "danger", "info"].map((variant) => (
          <UI.Button key={variant} variant={variant} size="sm">{variant}</UI.Button>
        ))}
      </div>
    </div>
  );
}

function Inputs() {
  const [value, setValue] = useState("");
  return (
    <div className={FIELDS}>
      <UI.Input label="Nombre del reporte" placeholder="Disponibilidad por tienda" value={value} onChange={(e) => setValue(e.target.value)} helperText="Puedes editar este ejemplo." />
      <UI.Input label="Buscar producto" leftIcon={<Search size={15} />} placeholder="Nombre o SKU" />
      <UI.Input label="Campo con error" defaultValue="" error="Ingresa un nombre para continuar." />
      <UI.Input label="Solo lectura" defaultValue="Marca A" disabled />
    </div>
  );
}

function Selects() {
  const [v, setV] = useState("a");
  return (
    <div className={FIELDS}>
      <UI.Select label="Tienda" options={opts} value={v} onChange={(e) => setV(e.target.value)} />
      <UI.Select label="Con búsqueda" options={opts} searchable value={v} onChange={(e) => setV(e.target.value)} />
      <UI.Select label="Deshabilitado" options={opts} value="a" disabled onChange={() => {}} />
      <UI.Select label="Estado de error" options={opts} placeholder="Seleccionar" error="Selecciona una tienda." onChange={() => {}} />
    </div>
  );
}

function Multi() {
  const [v, setV] = useState(["a", "b"]);
  return <UI.MultiSelect label="Tiendas seleccionadas" options={opts} value={v} onChange={setV} searchable helperText="Busca, selecciona y elimina opciones." />;
}

function Textareas() {
  const [v, setV] = useState("Analizar los productos que llevan más de 15 días fuera.");
  return (
    <div className={FIELDS}>
      <UI.Textarea label="Descripción" value={v} onChange={(e) => setV(e.target.value)} showCount maxLength={180} />
      <UI.Textarea label="Notas" placeholder="Escribe una nota…" error="Completa la nota antes de guardar." />
    </div>
  );
}

function Checks() {
  const [v, setV] = useState(true);
  return (
    <div className={STACK}>
      <div className={ROW}>
        <UI.Checkbox label="Incluir publicaciones" checked={v} onCheckedChange={setV} />
        <UI.Checkbox label="Parcial" indeterminate />
        <UI.Checkbox label="Deshabilitado" disabled checked readOnly />
      </div>
      <UI.Checkbox label="Notificaciones" description="Activar o desactivar este ejemplo" toggle checked={v} onCheckedChange={setV} />
    </div>
  );
}

function Radios() {
  const [v, setV] = useState("sku");
  return (
    <div className={ROW}>
      {["sku", "publicación"].map((x) => (
        <UI.Radio key={x} name="unit-demo" value={x} label={`Por ${x}`} checked={v === x} onChange={() => setV(x)} />
      ))}
      <UI.Radio label="No disponible" disabled />
    </div>
  );
}

function Editor() {
  const [v, setV] = useState('{\n  "reporte": "Disponibilidad",\n  "periodo": 30\n}');
  return <UI.CodeEditor value={v} onValueChange={setV} language="json" label="Configuración de ejemplo" minHeight={140} showLineNumbers showCount showCopyButton />;
}

function Cards() {
  return (
    <div className={FIELDS}>
      <UI.Card noPadding>
        <UI.CardHeader action={<UI.Badge variant="success" pill>Activo</UI.Badge>}>Disponibilidad</UI.CardHeader>
        <UI.CardBody>
          <p className="mb-1 font-display text-[32px] font-extrabold text-positive">89,3<span className="text-[25px]">%</span></p>
          <p>Publicaciones disponibles en el período.</p>
        </UI.CardBody>
        <UI.CardFooter align="between"><span>Últimos 30 días</span><Btn size="sm">Ver reporte</Btn></UI.CardFooter>
      </UI.Card>
      <UI.Card hoverable>
        <h3 className="font-display text-[15px] font-extrabold text-neutral-900">Oportunidades de surtido</h3>
        <p className={DESC}>Superficie blanca, borde suave y una sola jerarquía de lectura.</p>
        <Btn size="sm" onClick={() => document.getElementById("gallery-search")?.focus()}>Explorar componentes</Btn>
      </UI.Card>
    </div>
  );
}

function Badges() {
  const label = { success: "✓ Disponible", error: "× Caído", warning: "! Atención" };
  return (
    <div className={ROW}>
      {["primary", "neutral", "success", "warning", "error", "info"].map((v) => (
        <UI.Badge key={v} variant={v} pill>{label[v] ?? v}</UI.Badge>
      ))}
    </div>
  );
}

function Avatars() {
  return (
    <div className={ROW}>
      {["xs", "sm", "md", "lg", "xl"].map((size, i) => (
        <UI.Avatar key={size} initials="Ana Pérez" size={size} status={["online", "offline", "busy", "away", "online"][i]} />
      ))}
    </div>
  );
}

function Alerts() {
  const titles = ["Información", "Listo", "Requiere atención", "No se pudo actualizar"];
  return (
    <div className={STACK}>
      {["info", "success", "warning", "error"].map((v, i) => (
        <UI.Alert key={v} variant={v} title={titles[i]} dismissible>Mensaje de ejemplo con icono y texto para identificar el estado.</UI.Alert>
      ))}
    </div>
  );
}

function TabsDemo() {
  const [v, setV] = useState("summary");
  return (
    <UI.Tabs items={items} activeId={v} onChange={setV} variant="default">
      {items.map((x) => (
        <UI.TabPanel id={x.id} key={x.id}>
          <div className="mt-3 flex items-center gap-2 rounded-[10px] border border-neutral-200 bg-canvas px-4 py-5 text-xs text-neutral-600">
            <Check size={15} />Estás viendo {x.label.toLowerCase()}.
          </div>
        </UI.TabPanel>
      ))}
    </UI.Tabs>
  );
}

function ReportTabs() {
  const [v, setV] = useState("summary");
  return (
    <div className={STACK}>
      <DashboardTabs tabs={items.map((x) => ({ id: x.id, label: { es: x.label }, color: undefined, section_ids: [] }))} activeId={v} onChange={setV} />
      <p className={DESC}>Selección: <b>{items.find((x) => x.id === v).label}</b>. También puedes usar ← →, Inicio y Fin.</p>
    </div>
  );
}

function Crumbs() {
  return <UI.Breadcrumb items={[{ label: "Inicio", href: "#" }, { label: "Marca A", href: "#" }, { label: "Disponibilidad" }]} showHomeIcon />;
}

function Pagination() {
  const [v, setV] = useState(2);
  return <UI.Pagination currentPage={v} totalPages={8} onChange={setV} totalItems={192} itemsPerPage={24} showInfo language="es" />;
}

function Table() {
  const [sort, setSort] = useState({ key: "retailer", direction: "asc" });
  const rows = [
    { id: 1, retailer: "Tienda A", coverage: 89.3, status: "Disponible" },
    { id: 2, retailer: "Tienda B", coverage: 76.8, status: "Revisar" },
    { id: 3, retailer: "Tienda C", coverage: 92.5, status: "Disponible" },
  ].sort((a, b) => (typeof a[sort.key] === "number" ? a[sort.key] - b[sort.key] : a[sort.key].localeCompare(b[sort.key])) * (sort.direction === "desc" ? -1 : 1));
  return (
    <UI.Table
      rowKey="id"
      columns={[
        { key: "retailer", accessor: "retailer", header: "Tienda", sortable: true },
        { key: "coverage", header: "Cobertura", sortable: true, align: "right", render: (r) => `${r.coverage}%` },
        { key: "status", header: "Estado", render: (r) => <UI.Badge variant={r.status === "Disponible" ? "success" : "warning"} pill>{r.status}</UI.Badge> },
      ]}
      data={rows}
      sortColumn={sort.key}
      sortDirection={sort.direction}
      onSort={(key, direction) => setSort({ key, direction })}
      hoverable
    />
  );
}

function Dropdown() {
  return (
    <UI.Dropdown trigger={<Btn>Opciones del reporte</Btn>}>
      <div className={`${STACK} p-3.5`}>
        <b>Vista del reporte</b>
        <UI.Checkbox label="Mostrar totales" defaultChecked />
        <UI.Checkbox label="Mostrar comparación" />
      </div>
    </UI.Dropdown>
  );
}

function Menu() {
  const toast = UI.useToast();
  return (
    <UI.DropdownMenu
      trigger={<Btn>Acciones <Settings size={14} /></Btn>}
      items={[
        { label: "Duplicar", value: "copy", onClick: () => toast.success("Se duplicó el ejemplo") },
        { label: "Exportar", value: "export", icon: <Download size={14} />, onClick: () => toast.info("Exportación de ejemplo") },
        { label: "No disponible", value: "disabled", disabled: true },
        { label: "Eliminar ejemplo", value: "delete", danger: true, dividerBefore: true, onClick: () => toast.warning("Se eliminó el ejemplo") },
      ]}
    />
  );
}

function Modal() {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("Disponibilidad");
  return (
    <>
      <Btn kind="primary" onClick={() => setOpen(true)}>Abrir modal</Btn>
      <UI.Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Guardar vista"
        description="Conserva una selección de filtros para volver a usarla."
        footer={<div className={ROW}><Btn onClick={() => setOpen(false)}>Cancelar</Btn><Btn kind="primary" onClick={() => setOpen(false)}>Guardar vista</Btn></div>}
      >
        <UI.Input label="Nombre de la vista" value={name} onChange={(e) => setName(e.target.value)} />
      </UI.Modal>
    </>
  );
}

function Confirm() {
  const [open, setOpen] = useState(false);
  const toast = UI.useToast();
  return (
    <>
      <Btn onClick={() => setOpen(true)}>Abrir confirmación</Btn>
      <UI.ConfirmModal
        open={open}
        onClose={() => setOpen(false)}
        title="Eliminar vista de ejemplo"
        message="Esta acción solo modifica el ejemplo de la galería."
        confirmText="Eliminar"
        cancelText="Cancelar"
        confirmVariant="danger"
        onConfirm={() => { setOpen(false); toast.success("Vista de ejemplo eliminada"); }}
      />
    </>
  );
}

function Toasts() {
  const toast = UI.useToast();
  return (
    <div className={STACK}>
      <div className={ROW}>
        {["success", "info", "warning", "error"].map((v) => <Btn key={v} size="sm" onClick={() => toast[v]("Mensaje de ejemplo", "Notificación")}>{v}</Btn>)}
      </div>
      <UI.Toast
        toast={{ id: "inline-toast", variant: "success", message: "La vista de ejemplo está lista.", title: "Cambios guardados", duration: 0 }}
        onClose={() => toast.info("Este es el ejemplo persistente de una notificación.")}
      />
    </div>
  );
}

function Panel() {
  const [open, setOpen] = useState(true);
  return (
    <div className="min-h-[280px] max-w-[450px] overflow-hidden rounded-[14px] border border-neutral-200 [&>div]:min-h-[280px]">
      {open ? (
        <UI.SidePanel title="Detalle del producto" description="Información complementaria" onClose={() => setOpen(false)} footer={<Btn onClick={() => setOpen(false)}>Cerrar panel</Btn>}>
          <UI.Badge variant="success" pill>Disponible</UI.Badge>
          <h3 className="mb-1 mt-4 text-lg font-bold text-neutral-900">Impresora modelo X</h3>
          <p>3 publicaciones · 2 tiendas</p>
          <UI.Input label="Nota" placeholder="Agregar nota…" />
        </UI.SidePanel>
      ) : (
        <Btn onClick={() => setOpen(true)}>Abrir panel</Btn>
      )}
    </div>
  );
}

const shellReports = [
  { id: "r-disp", label: "Disponibilidad", icon: <LayoutDashboard size={16} /> },
  { id: "r-pricing", label: "Pricing", icon: <BarChart3 size={16} /> },
  { id: "r-share-search", label: "Share of Search", icon: <Search size={16} /> },
  { id: "r-eps", label: "ePerfect Store", icon: <Check size={16} /> },
];
const shellNav = [
  { id: "home", label: "Inicio", icon: <Home size={16} /> },
  { id: "__project__", kind: "project-selector", label: "" },
  { id: "__reports", kind: "section", label: "Reportes", badge: 4, children: shellReports },
  { id: "maestros", label: "Maestros", icon: <Database size={16} /> },
  { id: "files", label: "Archivos", icon: <Folder size={16} /> },
  { id: "tasks", label: "Tareas", icon: <ListChecks size={16} />, badge: 2, dividerBefore: true },
  { id: "settings", label: "Configuración", icon: <Settings size={16} /> },
];
const shellProjects = {
  caption: "Proyecto",
  menuTitle: "Cambiar de proyecto",
  active: { id: "a", label: "Marca A", meta: "4 reportes" },
  options: [
    { id: "a", label: "Marca A", meta: "4 reportes" },
    { id: "b", label: "Marca B", meta: "3 reportes" },
    { id: "c", label: "Marca C", meta: "2 reportes" },
  ],
  onSelect: () => {},
};
const navTarget = (item) => !item.id.startsWith("__");
const SHELL_STAGE = "relative overflow-hidden rounded-[14px] border border-neutral-200 bg-canvas";
const SHELL_FRAME = "overflow-hidden rounded-xl border border-neutral-200";

function Account({ collapsed }) {
  const [lang, setLang] = useState("es");
  return (
    <UI.SidebarAccount
      collapsed={collapsed}
      name="Ana Pérez"
      detail="Administradora"
      version="3.1.0"
      languages={[{ code: "es" }, { code: "en" }, { code: "pt" }]}
      language={lang}
      onLanguageChange={setLang}
      onSignOut={() => {}}
    />
  );
}

function Sidebar() {
  const [v, setV] = useState("r-disp");
  const [collapsed, setCollapsed] = useState(false);
  return (
    <div className={STACK}>
      <div className={ROW}>
        <UI.Button size="sm" variant="secondary" onClick={() => setCollapsed((c) => !c)}>{collapsed ? "Expandir" : "Colapsar a riel de 64 px"}</UI.Button>
      </div>
      <div className={`${SHELL_STAGE} flex h-[760px] w-[290px]`}>
        <UI.Sidebar items={shellNav} activeId={v} onItemClick={(x) => navTarget(x) && setV(x.id)} projectSelector={shellProjects} collapsed={collapsed} footer={<Account />} footerCollapsed={<Account collapsed />} />
      </div>
    </div>
  );
}

function AccountDemo() {
  return (
    <div className={ROW}>
      <div className="w-[250px] overflow-hidden rounded-[14px] border border-navy bg-navy p-2.5"><Account /></div>
      <div className="w-16 overflow-hidden rounded-[14px] border border-navy bg-navy py-2.5"><Account collapsed /></div>
    </div>
  );
}

function BrandHeaderDemo() {
  return (
    <div className={STACK}>
      <div className={SHELL_FRAME}><UI.BrandHeader brandText="Marca A" label="Disponibilidad · Disponibilidad y cobertura" onMenuToggle={() => {}} /></div>
      <div className={SHELL_FRAME}><UI.BrandHeader brandText="Omnitok" label="Inicio" /></div>
    </div>
  );
}

function Layout() {
  const [active, setActive] = useState("r-disp");
  return (
    <div className={`${SHELL_STAGE} h-[700px] [&>[data-omni-shell]]:h-full [&>[data-omni-shell]]:min-h-0`}>
      <UI.Layout
        brandHeader={{ brandText: "Marca A", label: "Disponibilidad · Disponibilidad y cobertura" }}
        projectSelector={shellProjects}
        sidebarItems={shellNav}
        activeSidebarId={active}
        onSidebarItemClick={(item) => navTarget(item) && setActive(item.id)}
        sidebarFooter={<Account />}
        sidebarFooterCollapsed={<Account collapsed />}
      >
        <UI.Card><UI.CardHeader>Contenido del reporte</UI.CardHeader><UI.CardBody>La navegación, la cabecera y el contenido comparten los mismos tokens.</UI.CardBody></UI.Card>
      </UI.Layout>
    </div>
  );
}

function Login() {
  const [message, setMessage] = useState("");
  return (
    <div className={`${SHELL_STAGE} h-[700px] [&>[data-omni-login]]:h-full [&>[data-omni-login]]:min-h-0`}>
      <UI.LoginPage
        language="es"
        brandText="Marca A"
        heroTitle="Disponibilidad, precios y búsqueda en un solo lugar"
        heroDescription="Ingresa para ver los reportes de tu marca en cada tienda."
        onLoginSubmit={() => setMessage("Inicio de sesión de ejemplo. No se envían credenciales.")}
        showRememberMe={false}
        showForgotPassword={false}
        footer={message || "Vista de ejemplo · no conecta a una cuenta"}
      />
    </div>
  );
}

function Dropzone() {
  const [count, setCount] = useState(0);
  return (
    <div className={STACK}>
      <UI.FileDropzone accept={{ "image/*": [".png", ".jpg"] }} onFilesAccepted={(files) => setCount(files.length)} maxFiles={3} showPreview />
      <p className={DESC}>{count} archivos seleccionados · solo en este navegador.</p>
    </div>
  );
}

function FilterHeader() {
  const [v, setV] = useState({ brand: "Marca A", retailer: "" });
  return (
    <div className="rounded-[14px] border border-neutral-200 bg-white p-3.5">
      <CollapsibleFilterHeader
        filters={[
          { key: "brand", column: "brand", label: { es: "Marca" }, type: "select", default_value: "Marca A" },
          { key: "retailer", column: "retailer", label: { es: "Tienda" }, type: "select" },
        ]}
        activeFilters={v}
        filterOptions={{ brand: ["Marca A", "Marca B"], retailer: ["Tienda A", "Tienda B", "Tienda C"] }}
        onFilterChange={(key, value) => setV((p) => ({ ...p, [key]: value }))}
        onClear={() => setV({ brand: "Marca A", retailer: "" })}
        onResetFilter={(f) => setV((p) => ({ ...p, [f.key]: "" }))}
        today={new Date("2026-10-20")}
        surface="light"
      />
    </div>
  );
}

function States() {
  return (
    <div className={STACK}>
      <SectionHeader title="Detalle por tienda" subtitle="Cabecera de sección con información de apoyo." info="La explicación completa queda disponible al enfocar este icono." />
      <MessageCard title="Sin resultados">No hay publicaciones para esta selección.</MessageCard>
      <MessageCard title="No se pudo actualizar" tone="error">Intenta nuevamente. Los datos anteriores se conservan.</MessageCard>
    </div>
  );
}

const entry = (id, category, title, Component, covers, subtitle = "Componente real · interacción local", source = "ui") => ({
  id: `ui-${id}`,
  category,
  title,
  componentName: covers[0],
  covers,
  subtitle,
  source,
  render: () => <Component />,
});

export const KIT_ENTRIES = [
  entry("button", "controls", "Botones", Buttons, ["Button"], "Acciones, tamaños, carga y deshabilitado"),
  entry("input", "controls", "Campos de texto", Inputs, ["Input"], "Vacío, editable, error y deshabilitado"),
  entry("select", "controls", "Selección simple", Selects, ["Select"]),
  entry("multi", "controls", "Selección múltiple", Multi, ["MultiSelect"]),
  entry("textarea", "controls", "Área de texto", Textareas, ["Textarea"]),
  entry("checkbox", "controls", "Checkbox y switch", Checks, ["Checkbox"]),
  entry("radio", "controls", "Radio", Radios, ["Radio"]),
  entry("editor", "controls", "Editor de código", Editor, ["CodeEditor"]),
  entry("cards", "surfaces", "Tarjetas", Cards, ["Card", "CardHeader", "CardBody", "CardFooter"]),
  entry("badges", "surfaces", "Badges y estados", Badges, ["Badge"]),
  entry("avatars", "surfaces", "Avatares", Avatars, ["Avatar"]),
  entry("alerts", "surfaces", "Alertas", Alerts, ["Alert"]),
  entry("tabs", "navigation", "Pestañas", TabsDemo, ["Tabs", "TabPanel"], "Opciones visibles, hover, selección y contenido"),
  entry("report-tabs", "navigation", "Pestañas del reporte", ReportTabs, ["DashboardTabs"], "El mismo componente de los reportes, con navegación por teclado", "render"),
  entry("crumbs", "navigation", "Breadcrumbs", Crumbs, ["Breadcrumb"]),
  entry("pagination", "navigation", "Paginación", Pagination, ["Pagination"]),
  entry("dropdown", "navigation", "Dropdown", Dropdown, ["Dropdown"]),
  entry("menu", "navigation", "Menú de acciones", Menu, ["DropdownMenu"]),
  entry("table", "tables", "Tabla general", Table, ["Table"], "Ordenamiento real, estados y columnas numéricas"),
  entry("modal", "feedback", "Modal", Modal, ["Modal"]),
  entry("confirm", "feedback", "Confirmación", Confirm, ["ConfirmModal"]),
  entry("toast", "feedback", "Toast", Toasts, ["Toast", "ToastContainer", "ToastProvider"]),
  entry("panel", "feedback", "Panel lateral", Panel, ["SidePanel"]),
  entry("states", "feedback", "Estados de contenido", States, ["SectionHeader", "MessageCard"], "Componente real · interacción local", "render"),
  entry("sidebar", "layout", "Navegación lateral", Sidebar, ["Sidebar"]),
  entry("brandheader", "layout", "Cabecera de marca", BrandHeaderDemo, ["BrandHeader"]),
  entry("account", "layout", "Cuenta en el sidebar", AccountDemo, ["SidebarAccount"]),
  entry("layout", "layout", "Layout de aplicación", Layout, ["Layout"]),
  entry("login", "layout", "Inicio de sesión", Login, ["LoginPage"]),
  entry("dropzone", "drag", "Carga de archivos", Dropzone, ["FileDropzone"]),
  entry("cajon", "filters", "Filtros colapsables", FilterHeader, ["CollapsibleFilterHeader"], "Componente real · interacción local", "render"),
];

/** Kit exports that are utilities, not components (no demo). */
export const KIT_EXCLUDED = ["cn", "createTranslator", "defaultMessages", "useI18n", "useToast", "useFocusTrap", "I18nProvider"];
/** Kit exports without a gallery entry: the gallery shows them as a defect. */
export const KIT_MISSING = Object.keys(UI).filter(
  (k) => !KIT_EXCLUDED.includes(k) && !KIT_ENTRIES.some((e) => e.covers.includes(k)),
);
